// src/pages/QbusAutomations/DebugPanel.jsx
// Node-RED-style debug panel with variable tree, copy path/value, execution timeline
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Collapse,
  InputAdornment
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
  Search as SearchIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as RunningIcon,
  DataObject as ObjectIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const statusIcons = {
  completed: <SuccessIcon sx={{ fontSize: 14, color: '#2e7d32' }} />,
  failed: <ErrorIcon sx={{ fontSize: 14, color: '#c62828' }} />,
  running: <RunningIcon sx={{ fontSize: 14, color: '#ed6c02' }} />,
};

// Copy text to clipboard with visual feedback
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).catch(() => {});
};

// Format a value for display
const formatValue = (val) => {
  if (val === null) return 'null';
  if (val === undefined) return 'undefined';
  if (typeof val === 'string') return val.length > 80 ? `"${val.substring(0, 80)}..."` : `"${val}"`;
  if (typeof val === 'boolean') return String(val);
  if (typeof val === 'number') return String(val);
  if (Array.isArray(val)) return `Array(${val.length})`;
  if (typeof val === 'object') return `{${Object.keys(val).length} keys}`;
  return String(val);
};

// Get the color for a value type
const valueColor = (val) => {
  if (val === null || val === undefined) return '#9e9e9e';
  if (typeof val === 'string') return '#c62828';
  if (typeof val === 'boolean') return '#1565c0';
  if (typeof val === 'number') return '#2e7d32';
  return '#616161';
};

// Recursive variable tree node
const VarTreeNode = ({ name, value, path, depth = 0, searchTerm, highlightPaths }) => {
  const [expanded, setExpanded] = useState(depth < 1);
  const isExpandable = value !== null && typeof value === 'object';
  const isHighlighted = highlightPaths?.has(path);

  const fullPath = path;
  const templatePath = `{{${fullPath}}}`;

  // Filter by search term
  const matchesSearch = !searchTerm ||
    fullPath.toLowerCase().includes(searchTerm.toLowerCase()) ||
    formatValue(value).toLowerCase().includes(searchTerm.toLowerCase());

  // For expandable nodes, check if any child matches
  const childEntries = isExpandable ? Object.entries(value) : [];
  const hasMatchingChild = searchTerm && childEntries.some(([k]) => {
    const childPath = `${fullPath}.${k}`;
    return childPath.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (searchTerm && !matchesSearch && !hasMatchingChild && !isExpandable) return null;

  // Auto-expand when searching
  const isExpanded = searchTerm ? (hasMatchingChild || expanded) : expanded;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: depth * 1.5,
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover' },
          '&:hover .copy-buttons': { opacity: 1 },
          bgcolor: isHighlighted ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
          borderLeft: isHighlighted ? '2px solid #2e7d32' : '2px solid transparent',
          cursor: isExpandable ? 'pointer' : 'default',
        }}
        onClick={() => isExpandable && setExpanded(!isExpanded)}
      >
        {/* Expand/collapse icon */}
        <Box sx={{ width: 18, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {isExpandable ? (
            isExpanded ? <ExpandIcon sx={{ fontSize: 14, color: 'text.secondary' }} /> : <CollapseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          ) : (
            <Box sx={{ width: 14 }} />
          )}
        </Box>

        {/* Key name */}
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'text.primary',
            mr: 0.5,
            flexShrink: 0,
          }}
        >
          {name}
        </Typography>

        {/* Colon separator */}
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.65rem' }}>:</Typography>

        {/* Value */}
        {!isExpandable && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.65rem',
              color: valueColor(value),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {formatValue(value)}
          </Typography>
        )}

        {/* Type indicator for expandable */}
        {isExpandable && (
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
            {formatValue(value)}
          </Typography>
        )}

        {/* Copy buttons (shown on hover) */}
        <Box className="copy-buttons" sx={{ display: 'flex', gap: 0, ml: 'auto', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}>
          <Tooltip title={`Copy path: ${templatePath}`} arrow placement="top">
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(templatePath);
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.55rem', fontFamily: 'monospace', color: 'primary.main' }}>
                {'{{}}'}
              </Typography>
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy value" arrow placement="top">
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={(e) => {
                e.stopPropagation();
                const copyVal = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '');
                copyToClipboard(copyVal);
              }}
            >
              <CopyIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Children */}
      {isExpandable && (
        <Collapse in={isExpanded}>
          {childEntries.map(([key, val]) => (
            <VarTreeNode
              key={key}
              name={key}
              value={val}
              path={`${fullPath}.${key}`}
              depth={depth + 1}
              searchTerm={searchTerm}
              highlightPaths={highlightPaths}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const DebugPanel = ({ execution, executionMap, onStepClick, selectedNodeId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showTimeline, setShowTimeline] = useState(true);

  // Get final variables from the last step's outputVariables
  const finalVariables = useMemo(() => {
    if (!execution?.execution_log?.length) return {};
    const steps = execution.execution_log;
    // Find the last completed step with outputVariables
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].outputVariables) return steps[i].outputVariables;
    }
    return {};
  }, [execution]);

  // Group variables by top-level key for cleaner display
  const topLevelVars = useMemo(() => {
    const grouped = {};
    for (const [key, value] of Object.entries(finalVariables)) {
      // Skip flattened dot-notation keys (they're redundant with the object tree)
      if (key.includes('.')) continue;
      // Skip internal keys
      if (key.startsWith('_')) continue;
      grouped[key] = value;
    }
    return grouped;
  }, [finalVariables]);

  const steps = execution?.execution_log || [];

  if (!execution) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Select an execution to view debug data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#263238', color: 'white' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
          Debug Inspector
        </Typography>
      </Box>

      {/* Execution Steps Timeline */}
      <Box sx={{ px: 1, pt: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 0.5 }}
          onClick={() => setShowTimeline(!showTimeline)}
        >
          {showTimeline ? <ExpandIcon sx={{ fontSize: 14 }} /> : <CollapseIcon sx={{ fontSize: 14 }} />}
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary' }}>
            Steps ({steps.length})
          </Typography>
        </Box>
        <Collapse in={showTimeline}>
          <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 1 }}>
            {steps.map((step, i) => {
              const duration = step.startedAt && step.finishedAt
                ? `${((new Date(step.finishedAt) - new Date(step.startedAt)) / 1000).toFixed(1)}s`
                : '';
              const isSelected = step.nodeId === selectedNodeId;
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.4,
                    px: 0.5,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 0.5,
                  }}
                  onClick={() => onStepClick?.(step)}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', width: 16, textAlign: 'right', flexShrink: 0 }}>
                    {i + 1}
                  </Typography>
                  {statusIcons[step.status] || statusIcons.running}
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {step.label || step.blockType}
                  </Typography>
                  {duration && (
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary', flexShrink: 0 }}>
                      {duration}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>

      <Divider />

      {/* Variable Tree */}
      <Box sx={{ px: 1, pt: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Variables
        </Typography>
        <TextField
          size="small"
          placeholder="Search variables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14 }} /></InputAdornment>,
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: '0.7rem', py: 0 },
          }}
          inputProps={{ style: { padding: '4px 0' } }}
        />
      </Box>

      {/* Variable tree list */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5 }}>
        {Object.keys(topLevelVars).length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            No variables in this execution
          </Typography>
        ) : (
          Object.entries(topLevelVars).map(([key, value]) => (
            <VarTreeNode
              key={key}
              name={key}
              value={value}
              path={key}
              depth={0}
              searchTerm={searchTerm}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

// Exported for reuse in NodeDataDrawer
export { VarTreeNode, formatValue, valueColor, copyToClipboard };
export default DebugPanel;
